/* Client-side search index across the two guides, FAQs and free tools.
   All content is static, so we build the index once at module load.

   Enhancements:
   - synonyms:      "SDLT" / "stamp duty" / "AIP" / "LTV" / "overpayment" etc.
     get boosted or expanded so obvious searches surface the right tool.
   - fuzzy:         single-character typo tolerance on tokens of length >= 4
     (Damerau-like via 1-char edit distance) so "stamp dutty" still matches
     "stamp duty".
   - accessibility: the dialog labels its input, keeps a stable roving focus
     order and announces results via aria-live in the component. */
import { FAQS, FTB_SECTIONS, REMO_SECTIONS } from "../content/site";

const sectionText = (s) =>
  [s.intro, ...(s.paragraphs || []), ...(s.bullets || []), ...(s.items || []).map((it) => `${it.h}. ${it.b}`)]
    .filter(Boolean)
    .join(" ");

/* Manual promotions: when the user's query contains one of these terms,
   inject the target result at the top of the list. Keeps obvious searches
   like "SDLT" or "AIP" from being buried by paragraph-body hits. */
const PROMOTIONS = [
  { match: /(^|\W)(stamp\s*duty|sdlt|ltt|lbtt|property\s*tax|buying\s*tax)(\W|$)/i, href: "/stamp-duty-calculator" },
  { match: /(^|\W)(aip|agreement\s*in\s*principle|decision\s*in\s*principle|mortgage\s*in\s*principle)(\W|$)/i, href: "/first-time-buyer-guide#aip" },
  { match: /(^|\W)(survey|surveyor|homebuyer\s*report|rics)(\W|$)/i, href: "/first-time-buyer-guide#surveys" },
  { match: /(^|\W)(conveyanc|solicitor|legal\s*work)/i, href: "/first-time-buyer-guide#conveyancing" },
  { match: /(^|\W)(remortgage|product\s*transfer|erc)/i, href: "/remortgage-guide" },
  { match: /(^|\W)(overpayment|overpay|clear\s*mortgage\s*early)/i, href: "/calculator" },
  { match: /(^|\W)(ltv|loan[-\s]to[-\s]value|deposit\s*band)/i, href: "/calculator#ltv" },
];

const TOOLS = [
  {
    type: "Tool",
    title: "Stamp Duty & Property Tax Calculator",
    text: "Estimate stamp duty land tax SDLT, land transaction tax LTT (Wales) or land and buildings transaction tax LBTT (Scotland) on a UK property purchase. Covers first-time buyer relief, additional property and buy-to-let higher rates, the non-resident surcharge, buying costs and upfront property tax for England, Northern Ireland, Wales and Scotland.",
    href: "/stamp-duty-calculator",
    keywords: "stamp duty sdlt ltt lbtt property tax buying costs first-time buyer relief additional property surcharge",
  },
  {
    type: "Tool",
    title: "UK Mortgage Calculator",
    text: "UK mortgage repayment calculator. Estimate monthly repayments, total interest, loan-to-value LTV and the impact of monthly overpayments on the term and total interest paid.",
    href: "/calculator",
    keywords: "mortgage calculator overpayment ltv loan-to-value monthly repayment interest",
  },
  {
    type: "Tool",
    title: "Free Mortgage Snapshot",
    text: "Get a free educational snapshot of the key mortgage figures — estimated loan, monthly repayment and loan-to-value. Educational only, not regulated mortgage advice.",
    href: "/snapshot",
    keywords: "snapshot affordability borrowing capacity mortgage figures",
  },
  {
    type: "Tool",
    title: "Property Survey Quote",
    text: "Request a no-obligation property survey quote — RICS Level 2 Home Survey (formerly HomeBuyer Report), RICS Level 3 Home Survey (Building Survey), independent valuation or scheme valuation.",
    href: "/survey-quote",
    keywords: "survey rics level 2 level 3 homebuyer report building survey valuation home buyer",
  },
  {
    type: "Tool",
    title: "Conveyancing Quote",
    text: "Request a conveyancing quote for a purchase, sale or remortgage. MortgagePath refers to a selected conveyancing partner.",
    href: "/conveyancing-quote",
    keywords: "conveyancing solicitor licensed conveyancer legal purchase remortgage",
  },
];

const INDEX = [
  ...TOOLS,
  ...FAQS.map((f, i) => ({ type: "FAQ", title: f.q, text: f.a, href: `/faqs#faq-${i}` })),
  ...FTB_SECTIONS.map((s) => ({
    type: "First-Time Buyer Guide",
    title: s.h,
    text: sectionText(s),
    href: `/first-time-buyer-guide#${s.id}`,
  })),
  ...REMO_SECTIONS.map((s) => ({
    type: "Remortgage Guide",
    title: s.h,
    text: sectionText(s),
    href: `/remortgage-guide#${s.id}`,
  })),
];

export const termsFrom = (query) =>
  query.toLowerCase().trim().split(/\s+/).filter((t) => t.length > 1);

// One-character edit distance (insert / delete / replace / transpose adjacent).
// Not full Levenshtein — a short-circuit implementation good enough for typo
// tolerance on tokens length >= 4.
const oneEditAway = (a, b) => {
  if (a === b) return true;
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  // adjacent transposition
  if (la === lb) {
    let diffs = 0;
    for (let i = 0; i < la; i++) if (a[i] !== b[i]) diffs++;
    if (diffs <= 1) return true;
    // check single adjacent swap
    for (let i = 0; i < la - 1; i++) {
      if (a[i] !== b[i] && a[i] === b[i + 1] && a[i + 1] === b[i]) {
        // rest must match
        const s1 = a.slice(0, i) + a.slice(i + 2);
        const s2 = b.slice(0, i) + b.slice(i + 2);
        if (s1 === s2) return true;
      }
    }
    return false;
  }
  // insertion/deletion — walk the longer once
  const [s, l] = la < lb ? [a, b] : [b, a];
  let i = 0, j = 0, edits = 0;
  while (i < s.length && j < l.length) {
    if (s[i] !== l[j]) {
      if (++edits > 1) return false;
      j++;
    } else {
      i++; j++;
    }
  }
  return true;
};

const tokenMatches = (haystack, term) => {
  if (haystack.includes(term)) return true;
  if (term.length < 4) return false;
  // check any word in the haystack within 1 edit of term
  const words = haystack.split(/[^a-z0-9]+/);
  for (const w of words) {
    if (w && Math.abs(w.length - term.length) <= 1 && oneEditAway(w, term)) return true;
  }
  return false;
};

const makeSnippet = (text, terms) => {
  const lower = text.toLowerCase();
  let idx = -1;
  for (const t of terms) {
    idx = lower.indexOf(t);
    if (idx !== -1) break;
  }
  if (idx === -1) return text.slice(0, 140) + (text.length > 140 ? "…" : "");
  const start = Math.max(0, idx - 50);
  const end = Math.min(text.length, idx + 110);
  return (start > 0 ? "…" : "") + text.slice(start, end).trim() + (end < text.length ? "…" : "");
};

const promotionHrefs = (query) => {
  const hits = [];
  for (const p of PROMOTIONS) if (p.match.test(query)) hits.push(p.href);
  return hits;
};

export const searchContent = (query, limit = 12) => {
  const terms = termsFrom(query);
  if (!terms.length) return [];
  const promoted = promotionHrefs(query);
  const scored = [];
  for (const entry of INDEX) {
    const title = entry.title.toLowerCase();
    const text = entry.text.toLowerCase();
    const keywords = (entry.keywords || "").toLowerCase();
    let score = 0;
    let ok = true;
    for (const t of terms) {
      const inTitle = tokenMatches(title, t);
      const inKw = tokenMatches(keywords, t);
      const inText = tokenMatches(text, t);
      if (!inTitle && !inKw && !inText) { ok = false; break; }
      score += inTitle ? 12 : inKw ? 8 : 1;
    }
    if (!ok) continue;
    if (promoted.includes(entry.href)) score += 100;
    scored.push({ ...entry, score, snippet: makeSnippet(entry.text, terms) });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
};
